/**
 * apps/battle/client/src/admin/KnobsScreen.tsx — Knobs tab (Phase 4.2 M3 patch2).
 *
 * Three sub-tabs:
 *   Runtime          → battle.content_status (DRAFT|ACTIVE) toggle
 *   Grading model    → grade.{primary,fallback}.{provider,model,base_url,timeout_ms}
 *                      (API keys remain Fly secrets — see comment block below)
 *   Prompts          → prompt.points.{system,user}, prompt.levels.{system,user}
 *
 * Patch2 (Option A defaults):
 *   • Every field is pre-filled with the SERVER-RESOLVED effective value
 *     (DB value if present, else engine default). No more "leave blank for
 *     default" guesswork.
 *   • A small chip next to each label says "(default)" when no DB override
 *     exists, or "(custom)" when an admin has saved a value.
 *   • Each row has a "Reset to default" button that calls DELETE
 *     /api/admin/config/:key. The button is disabled when isCustom = false.
 *   • Prompt key naming fixed: the Prompts tab now reads/writes
 *     prompt.points.user / prompt.levels.user (matching what the runtime
 *     actually reads in apps/battle/src/server/runtime.ts §getPromptOverrides).
 *
 * Loads the full system_config snapshot once (GET /api/admin/config). Every
 * save is an individual PUT call so partial saves are safe.
 */
import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../api.js';
import type {
  ConfigCategory,
  ConfigRow,
  ConfigValueType,
  DeleteConfigOkResp,
  ListConfigResp,
  PutConfigOkResp,
} from './api.js';

interface Props {
  showToast: (msg: string, kind?: 'success' | 'error' | 'warn') => void;
}

type SubTab = 'runtime' | 'grading' | 'prompts' | 'comms';

// Provider whitelist matches buildGradingProvider() on the server.
const PROVIDER_OPTIONS = ['deepseek', 'openai', 'qwen', 'mock'] as const;

// The four prompt keys the engine reads (see
// apps/battle/src/server/runtime.ts §getPromptOverrides + the
// PromptTemplateOverrides interface in packages/battle/src/prompts.ts).
const PROMPT_KEYS: Array<{ key: string; label: string; help: string }> = [
  {
    key: 'prompt.points.system',
    label: 'Points · system',
    help: 'System prompt for short-answer (1-mark / points-based) grading. Placeholder: {{marks}}.',
  },
  {
    key: 'prompt.points.user',
    label: 'Points · user',
    help: 'User prompt template. Placeholders: {{stem}}, {{command_word}}, {{marks}}, {{points_text}}, {{model_answer_section}}, {{student_answer}}.',
  },
  {
    key: 'prompt.levels.system',
    label: 'Levels · system',
    help: 'System prompt for level-marked (≥2 marks) grading. Placeholders: {{marks}}, {{max_level}}.',
  },
  {
    key: 'prompt.levels.user',
    label: 'Levels · user',
    help: 'User prompt template. Placeholders: {{stem}}, {{command_word}}, {{marks}}, {{levels_text}}, {{indicative_content}}, {{model_answer_section}}, {{student_answer}}.',
  },
  {
    key: 'prompt.suggest.system',
    label: 'Suggest edits · system',
    help: "Editable guidance for the AI 'Suggest edits' prompt (persona + policy). The JSON output contract and PM-locked fields are fixed in code and not editable here. Leave blank to use the default text-only guidance.",
  },
];

// The 12 grade.* knobs. Each tile carries the descriptor used by PUT.
// M15 added the thinking / max_tokens pair per role.
const GRADE_KEYS: Array<{
  key: string;
  label: string;
  valueType: ConfigValueType;
  kind: 'provider' | 'model' | 'base_url' | 'timeout_ms' | 'thinking' | 'max_tokens';
  // M15: literal test id (not derived from the key) so a static smoke grep can
  // match the full string in the built bundle — esbuild splits template-built
  // ids. Same precedent as COUNTDOWN_KEYS above.
  inputTestId?: string;
}> = [
  { key: 'grade.primary.provider', label: 'Primary provider', valueType: 'string', kind: 'provider' },
  { key: 'grade.primary.model', label: 'Primary model', valueType: 'string', kind: 'model' },
  { key: 'grade.primary.base_url', label: 'Primary base URL (optional)', valueType: 'string', kind: 'base_url' },
  { key: 'grade.primary.timeout_ms', label: 'Primary timeout (ms)', valueType: 'number', kind: 'timeout_ms' },
  { key: 'grade.primary.thinking', label: 'Primary thinking', valueType: 'string', kind: 'thinking', inputTestId: 'input-grade.primary.thinking' },
  { key: 'grade.primary.max_tokens', label: 'Primary max_tokens (0 = omit)', valueType: 'number', kind: 'max_tokens', inputTestId: 'input-grade.primary.max_tokens' },
  { key: 'grade.fallback.provider', label: 'Fallback provider', valueType: 'string', kind: 'provider' },
  { key: 'grade.fallback.model', label: 'Fallback model', valueType: 'string', kind: 'model' },
  { key: 'grade.fallback.base_url', label: 'Fallback base URL (optional)', valueType: 'string', kind: 'base_url' },
  { key: 'grade.fallback.timeout_ms', label: 'Fallback timeout (ms)', valueType: 'number', kind: 'timeout_ms' },
  { key: 'grade.fallback.thinking', label: 'Fallback thinking', valueType: 'string', kind: 'thinking', inputTestId: 'input-grade.fallback.thinking' },
  { key: 'grade.fallback.max_tokens', label: 'Fallback max_tokens (0 = omit)', valueType: 'number', kind: 'max_tokens', inputTestId: 'input-grade.fallback.max_tokens' },
];

// M15 reasoning modes. 'off' sends the vendor's documented disable flag;
// 'on' OMITS the parameter, restoring the pre-M15 request shape byte-for-byte.
const THINKING_OPTIONS = ['off', 'on'] as const;

// M12i — exam countdown (COMMS): two free-text string knobs. The widget on the
// student dashboard renders only when BOTH are set (name + ISO date).
const COUNTDOWN_KEYS: Array<{
  key: string;
  // Literal test id (not derived from key) so static smoke greps can match the
  // full string in the built bundle — template-built ids get split by esbuild.
  inputTestId: string;
  label: string;
  category: ConfigCategory;
  description: string;
  placeholder: string;
  hint?: string;
}> = [
  {
    key: 'exam.countdown.name',
    inputTestId: 'input-exam.countdown.name',
    label: 'exam.countdown.name',
    category: 'COMMS',
    description: 'Exam countdown display label shown on the student dashboard',
    placeholder: 'Paper 1 June 2027 (predicted)',
  },
  {
    key: 'exam.countdown.date',
    inputTestId: 'input-exam.countdown.date',
    label: 'exam.countdown.date',
    category: 'COMMS',
    description: 'Exam date, ISO YYYY-MM-DD',
    placeholder: '2027-05-08',
    hint: 'Format: YYYY-MM-DD',
  },
];

// ---- Helpers --------------------------------------------------------------

/** Render any unknown value to a string for an <input>/<textarea>. */
function valueToString(v: unknown, valueType: ConfigValueType): string {
  if (v === null || v === undefined) return '';
  if (valueType === 'number') return Number.isFinite(v as number) ? String(v) : String(v ?? '');
  if (typeof v === 'string') return v;
  return String(v);
}

/** Status chip — green "(default)" vs blue "(custom)". */
function StatusChip({ isCustom }: { isCustom: boolean }) {
  return (
    <span className={`knob-status ${isCustom ? 'custom' : 'default'}`}>
      {isCustom ? '(custom)' : '(default)'}
    </span>
  );
}

// ---- Main screen ----------------------------------------------------------

export function KnobsScreen({ showToast }: Props) {
  const [tab, setTab] = useState<SubTab>('runtime');
  const [rows, setRows] = useState<ConfigRow[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoadErr(null);
    api
      .get<ListConfigResp>('/api/admin/config')
      .then((r) => { if (!cancelled) setRows(r.rows); })
      .catch((err) => {
        if (cancelled) return;
        const msg = err instanceof ApiError ? `${err.status} ${err.message}` : String(err);
        setLoadErr(msg);
      });
    return () => { cancelled = true; };
  }, [reloadTick]);

  const byKey = useMemo(() => {
    const m = new Map<string, ConfigRow>();
    for (const r of rows ?? []) m.set(r.key, r);
    return m;
  }, [rows]);

  async function saveOne(
    key: string,
    value: unknown,
    valueType: ConfigValueType,
    category: ConfigCategory,
    description?: string,
  ): Promise<void> {
    try {
      const resp = await api.put<PutConfigOkResp>('/api/admin/config', {
        key, value, valueType, category, description,
      });
      if (resp.warning) {
        showToast(`Saved ${key} — but ${resp.warning}: ${resp.detail ?? ''}`, 'warn');
      } else {
        showToast(`Saved ${key}`, 'success');
      }
      setReloadTick((n) => n + 1);
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status} ${err.message}` : String(err);
      if (err instanceof ApiError && err.status === 422 && err.body && typeof err.body === 'object') {
        const b = err.body as Record<string, unknown>;
        showToast(`Saved with warning: ${b.warning ?? 'invalid'} — ${b.detail ?? ''}`, 'warn');
        setReloadTick((n) => n + 1);
        return;
      }
      showToast(`Save failed: ${msg}`, 'error');
      throw err;
    }
  }

  async function resetOne(key: string): Promise<void> {
    try {
      const resp = await api.delete<DeleteConfigOkResp>(`/api/admin/config/${encodeURIComponent(key)}`);
      if (resp.warning) {
        showToast(`Reset ${key} — but ${resp.warning}: ${resp.detail ?? ''}`, 'warn');
      } else {
        showToast(`Reset ${key} to default`, 'success');
      }
      setReloadTick((n) => n + 1);
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status} ${err.message}` : String(err);
      showToast(`Reset failed: ${msg}`, 'error');
    }
  }

  return (
    <div data-testid="screen-knobs">
      <div className="tab-row">
        <button className={`tab ${tab === 'runtime' ? 'active' : ''}`} onClick={() => setTab('runtime')} data-testid="tab-runtime">
          Runtime
        </button>
        <button className={`tab ${tab === 'grading' ? 'active' : ''}`} onClick={() => setTab('grading')} data-testid="tab-grading">
          Grading model
        </button>
        <button className={`tab ${tab === 'prompts' ? 'active' : ''}`} onClick={() => setTab('prompts')} data-testid="tab-prompts">
          Prompts
        </button>
        <button className={`tab ${tab === 'comms' ? 'active' : ''}`} onClick={() => setTab('comms')} data-testid="tab-comms">
          Exam Countdown
        </button>
        <div style={{ flex: 1 }} />
        <button className="ghost sm" onClick={() => setReloadTick((n) => n + 1)} data-testid="button-refresh-config">
          Refresh
        </button>
      </div>

      {loadErr && <div className="err">Failed to load config: {loadErr}</div>}
      {!rows && !loadErr && <div className="spinner">Loading config…</div>}

      {rows && tab === 'runtime' && (
        <RuntimePanel byKey={byKey} saveOne={saveOne} resetOne={resetOne} />
      )}
      {rows && tab === 'grading' && (
        <GradingPanel byKey={byKey} saveOne={saveOne} resetOne={resetOne} />
      )}
      {rows && tab === 'prompts' && (
        <PromptsPanel byKey={byKey} saveOne={saveOne} resetOne={resetOne} />
      )}
      {rows && tab === 'comms' && (
        <CommsPanel byKey={byKey} saveOne={saveOne} resetOne={resetOne} />
      )}
    </div>
  );
}

// ---- Comms panel: exam countdown (M12i) -----------------------------------

function CommsPanel({ byKey, saveOne, resetOne }: PanelProps) {
  return (
    <div className="card">
      <h2>Exam Countdown</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Shows a two-line countdown in the top-right of the student dashboard. Both fields must be set —
        leave either blank to hide the widget. The date is parsed as ISO and days remaining are computed
        in the student's local timezone.
      </p>
      {COUNTDOWN_KEYS.map((spec) => (
        <StringKnobRow key={spec.key} spec={spec} row={byKey.get(spec.key)} saveOne={saveOne} resetOne={resetOne} />
      ))}
    </div>
  );
}

function StringKnobRow({
  spec,
  row,
  saveOne,
  resetOne,
}: {
  spec: (typeof COUNTDOWN_KEYS)[number];
  row: ConfigRow | undefined;
  saveOne: PanelProps['saveOne'];
  resetOne: PanelProps['resetOne'];
}) {
  const effective = typeof row?.effective === 'string' ? row.effective : '';
  const isCustom = row?.isCustom ?? false;

  const [text, setText] = useState(effective);
  useEffect(() => { setText(effective); }, [effective]);
  const dirty = text !== effective;

  return (
    <div style={{ marginBottom: 14 }}>
      <label>
        {spec.label} <StatusChip isCustom={isCustom} />
      </label>
      {spec.hint && (
        <p className="muted" style={{ fontSize: 11, margin: '0 0 6px' }}>{spec.hint}</p>
      )}
      <div className="toolbar">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={spec.placeholder}
          style={{ maxWidth: 320 }}
          data-testid={spec.inputTestId}
        />
        <button
          className="primary sm"
          disabled={!dirty}
          onClick={() => void saveOne(spec.key, text.trim(), 'string', spec.category, spec.description)}
          data-testid={`button-save-${spec.key}`}
        >
          Save
        </button>
        <button
          className="ghost sm"
          disabled={!isCustom}
          onClick={() => void resetOne(spec.key)}
          data-testid={`button-reset-${spec.key}`}
          title={isCustom ? 'Delete the DB override so the default (blank) applies' : 'Already at default'}
        >
          Reset to default
        </button>
        {row?.lastModified && (
          <span className="muted" style={{ fontSize: 11 }}>
            last modified: {new Date(row.lastModified).toISOString().slice(0, 16).replace('T', ' ')}
          </span>
        )}
      </div>
    </div>
  );
}

// ---- Runtime panel: content_status ----------------------------------------

interface PanelProps {
  byKey: Map<string, ConfigRow>;
  saveOne: (key: string, value: unknown, valueType: ConfigValueType, category: ConfigCategory, description?: string) => Promise<void>;
  resetOne: (key: string) => Promise<void>;
}

function RuntimePanel({ byKey, saveOne, resetOne }: PanelProps) {
  const row = byKey.get('battle.content_status');
  const effective = typeof row?.effective === 'string' ? row.effective : 'DRAFT';
  const [value, setValue] = useState<string>(effective);

  useEffect(() => { setValue(effective); }, [effective]);

  const dirty = value !== effective;
  const isCustom = row?.isCustom ?? false;

  return (
    <>
      <div className="card">
        <h2>Runtime</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          DRAFT hides all WIP content from students. ACTIVE goes live — flip on M4 day.
        </p>
        <label>
          battle.content_status <StatusChip isCustom={isCustom} />
        </label>
        <div className="toolbar">
          <select
            value={value}
            onChange={(e) => setValue(e.target.value)}
            style={{ maxWidth: 200 }}
            data-testid="select-content-status"
          >
            <option value="DRAFT">DRAFT</option>
            <option value="ACTIVE">ACTIVE</option>
          </select>
          <button
            className="primary sm"
            disabled={!dirty}
            onClick={() => void saveOne('battle.content_status', value, 'string', 'FEATURE_FLAGS', 'DRAFT during build, ACTIVE on M4 LIVE flip')}
            data-testid="button-save-content-status"
          >
            Save
          </button>
          <button
            className="ghost sm"
            disabled={!isCustom}
            onClick={() => void resetOne('battle.content_status')}
            data-testid="button-reset-content-status"
            title={isCustom ? 'Delete the DB override so the env default applies' : 'Already at default'}
          >
            Reset to default
          </button>
          {row?.lastModified && (
            <span className="muted" style={{ fontSize: 11 }}>
              last modified: {new Date(row.lastModified).toISOString().slice(0, 16).replace('T', ' ')}
            </span>
          )}
        </div>
      </div>

      <div className="card">
        <h2>Leaderboard &amp; Limits (M10)</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Master switch and size for the global leaderboard, the paste guard on battle answers, and the
          minimum days between display-name changes. Saving persists a DB override; Reset drops it back to the
          engine default.
        </p>
        {M10_KEYS.map((spec) => (
          <M10KnobRow key={spec.key} spec={spec} row={byKey.get(spec.key)} saveOne={saveOne} resetOne={resetOne} />
        ))}
      </div>
    </>
  );
}

// ---- M10 knobs: leaderboard / paste / name-change-days --------------------

const M10_KEYS: Array<{
  key: string;
  label: string;
  valueType: Extract<ConfigValueType, 'boolean' | 'number'>;
  category: ConfigCategory;
  description: string;
}> = [
  { key: 'leaderboard.enabled', label: 'leaderboard.enabled', valueType: 'boolean', category: 'FEATURE_FLAGS', description: 'Master switch for GET /api/leaderboard' },
  { key: 'battle.paste_enabled', label: 'battle.paste_enabled', valueType: 'boolean', category: 'FEATURE_FLAGS', description: 'When false, paste into battle answer inputs is blocked' },
  { key: 'leaderboard.top_n', label: 'leaderboard.top_n', valueType: 'number', category: 'LIMITS', description: 'Number of top players returned by the leaderboard' },
  { key: 'auth.display_name_change_days', label: 'auth.display_name_change_days', valueType: 'number', category: 'LIMITS', description: 'Minimum days between display-name changes' },
];

function M10KnobRow({
  spec,
  row,
  saveOne,
  resetOne,
}: {
  spec: (typeof M10_KEYS)[number];
  row: ConfigRow | undefined;
  saveOne: PanelProps['saveOne'];
  resetOne: PanelProps['resetOne'];
}) {
  const effective = row?.effective;
  const isCustom = row?.isCustom ?? false;

  // Booleans render as a select; numbers as a numeric input. Pre-fill from the
  // server-resolved effective value (DB override or engine default).
  const initialBool = effective === true || effective === 'true';
  const initialText = valueToString(effective, spec.valueType);

  const [boolValue, setBoolValue] = useState<boolean>(initialBool);
  const [text, setText] = useState<string>(initialText);
  useEffect(() => { setBoolValue(initialBool); }, [initialBool]);
  useEffect(() => { setText(initialText); }, [initialText]);

  const dirty = spec.valueType === 'boolean' ? boolValue !== initialBool : text !== initialText;

  async function onSave() {
    if (spec.valueType === 'boolean') {
      await saveOne(spec.key, boolValue, 'boolean', spec.category, spec.description);
      return;
    }
    if (text.trim() === '') return;
    const n = Number(text);
    if (!Number.isFinite(n)) return;
    await saveOne(spec.key, n, 'number', spec.category, spec.description);
  }

  return (
    <div style={{ marginBottom: 14 }}>
      <label>
        {spec.label} <StatusChip isCustom={isCustom} />
      </label>
      <div className="toolbar">
        {spec.valueType === 'boolean' ? (
          <select
            value={boolValue ? 'true' : 'false'}
            onChange={(e) => setBoolValue(e.target.value === 'true')}
            style={{ maxWidth: 200 }}
            data-testid={`input-${spec.key}`}
          >
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        ) : (
          <input
            type="number"
            value={text}
            onChange={(e) => setText(e.target.value)}
            style={{ maxWidth: 200 }}
            data-testid={`input-${spec.key}`}
          />
        )}
        <button
          className="primary sm"
          disabled={!dirty}
          onClick={() => void onSave()}
          data-testid={`button-save-${spec.key}`}
        >
          Save
        </button>
        <button
          className="ghost sm"
          disabled={!isCustom}
          onClick={() => void resetOne(spec.key)}
          data-testid={`button-reset-${spec.key}`}
          title={isCustom ? 'Delete the DB override so the engine default applies' : 'Already at default'}
        >
          Reset to default
        </button>
        {row?.lastModified && (
          <span className="muted" style={{ fontSize: 11 }}>
            last modified: {new Date(row.lastModified).toISOString().slice(0, 16).replace('T', ' ')}
          </span>
        )}
      </div>
    </div>
  );
}

// ---- Grading panel: 8 grade.* rows ----------------------------------------

function GradingPanel({ byKey, saveOne, resetOne }: PanelProps) {
  return (
    <div className="card">
      <h2>Grading model</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Same provider stack governs <code>/api/battle/submit</code> and <code>/api/ai/self-grade</code>. API keys
        remain Fly secrets (<code>GRADE_API_KEY</code>, <code>GRADE_FALLBACK_API_KEY</code>) and never appear here.
        Defaults shown reflect the matching <code>GRADE_*</code> Fly env. After a provider/model swap, open the
        Health tab and click "Probe grading provider".
      </p>
      <div className="knob-grid">
        {GRADE_KEYS.map((spec) => (
          <GradeRow key={spec.key} spec={spec} row={byKey.get(spec.key)} saveOne={saveOne} resetOne={resetOne} />
        ))}
      </div>
    </div>
  );
}

function GradeRow({
  spec,
  row,
  saveOne,
  resetOne,
}: {
  spec: (typeof GRADE_KEYS)[number];
  row: ConfigRow | undefined;
  saveOne: PanelProps['saveOne'];
  resetOne: PanelProps['resetOne'];
}) {
  const effective = row?.effective;
  const isCustom = row?.isCustom ?? false;

  // Pre-fill with the effective value (DB or env default).
  const initial = useMemo(
    () => valueToString(effective, spec.valueType),
    [effective, spec.valueType],
  );
  const [text, setText] = useState(initial);
  useEffect(() => { setText(initial); }, [initial]);
  const dirty = text !== initial;

  async function onSave() {
    let value: unknown = text;
    if (spec.valueType === 'number') {
      if (text.trim() === '') { value = 0; }
      else {
        const n = Number(text);
        if (!Number.isFinite(n)) return;
        value = n;
      }
    }
    await saveOne(spec.key, value, spec.valueType, 'LLM', spec.label);
  }

  return (
    <>
      <div className="k-key">
        {spec.key} <StatusChip isCustom={isCustom} />
      </div>
      <div>
        {spec.kind === 'provider' ? (
          <select value={text} onChange={(e) => setText(e.target.value)} data-testid={`input-${spec.key}`}>
            {/* Empty option still allowed — saving it pins an empty provider, but
                the field is normally pre-filled with the env default. */}
            <option value="">(empty — falls back to env)</option>
            {PROVIDER_OPTIONS.map((p) => (<option key={p} value={p}>{p}</option>))}
          </select>
        ) : spec.kind === 'thinking' ? (
          <select
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid={spec.inputTestId ?? `input-${spec.key}`}
          >
            {THINKING_OPTIONS.map((t) => (<option key={t} value={t}>{t}</option>))}
          </select>
        ) : (
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid={spec.inputTestId ?? `input-${spec.key}`}
          />
        )}
      </div>
      <div className="row-actions">
        <button
          className="primary sm"
          disabled={!dirty}
          onClick={() => void onSave()}
          data-testid={`button-save-${spec.key}`}
        >
          Save
        </button>
        <button
          className="ghost sm"
          disabled={!isCustom}
          onClick={() => void resetOne(spec.key)}
          data-testid={`button-reset-${spec.key}`}
          title={isCustom ? 'Delete the DB override so the env default applies' : 'Already at default'}
        >
          Reset
        </button>
      </div>
    </>
  );
}

// ---- Prompts panel --------------------------------------------------------

function PromptsPanel({ byKey, saveOne, resetOne }: PanelProps) {
  return (
    <div className="card">
      <h2>Prompts</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Each row is pre-filled with the engine default template (placeholder syntax shown). Edit any field and
        click Save to override; click Reset to drop your override and snap back to the engine default. Changes
        take effect on the next grading call (≤30s config cache).
      </p>
      {PROMPT_KEYS.map((p) => (
        <PromptRow key={p.key} spec={p} row={byKey.get(p.key)} saveOne={saveOne} resetOne={resetOne} />
      ))}
    </div>
  );
}

function PromptRow({
  spec,
  row,
  saveOne,
  resetOne,
}: {
  spec: (typeof PROMPT_KEYS)[number];
  row: ConfigRow | undefined;
  saveOne: PanelProps['saveOne'];
  resetOne: PanelProps['resetOne'];
}) {
  const effective = typeof row?.effective === 'string' ? row.effective : '';
  const isCustom = row?.isCustom ?? false;

  const [text, setText] = useState(effective);
  useEffect(() => { setText(effective); }, [effective]);
  const dirty = text !== effective;

  return (
    <div className="prompt-block">
      <div className="ph-title">
        <span className="name">{spec.key}</span>
        <StatusChip isCustom={isCustom} />
      </div>
      <p className="muted" style={{ fontSize: 11, margin: '0 0 6px' }}>{spec.help}</p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        data-testid={`input-${spec.key}`}
        rows={Math.min(20, Math.max(6, text.split('\n').length + 1))}
      />
      <div className="toolbar" style={{ marginTop: 6 }}>
        <button
          className="primary sm"
          disabled={!dirty}
          onClick={() => void saveOne(spec.key, text, 'string', 'LLM', spec.label)}
          data-testid={`button-save-${spec.key}`}
        >
          Save
        </button>
        <button
          className="ghost sm"
          disabled={!isCustom}
          onClick={() => void resetOne(spec.key)}
          data-testid={`button-reset-${spec.key}`}
          title={isCustom ? 'Delete the DB override so the engine default applies' : 'Already at default'}
        >
          Reset to default
        </button>
        {row?.lastModified && (
          <span className="muted" style={{ fontSize: 11 }}>
            last modified: {new Date(row.lastModified).toISOString().slice(0, 16).replace('T', ' ')}
          </span>
        )}
      </div>
    </div>
  );
}
